/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed, get } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import subjectTypes from '@linc-technologies/linced-common/enums/subject-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import topicSteps from '@linc-technologies/linced-common/utils/topic-steps';
import config from 'linced-app/config/environment';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

// BULK_DELETE_FAILED marks a bulk delete failure whose error was already
// shown, so the caller doesn't show a second one.
const BULK_DELETE_FAILED = new Error('Bulk delete failed');

// destroyOrRestore deletes a record, and on failure puts it back: undeleted,
// with any relationship edits reverted (such as a cleared categories list).
function destroyOrRestore(record) {
  return record.destroyRecord().catch((err) => {
    rollbackRecord(record);
    throw err;
  });
}

export default class SubjectController extends Controller {
  @controller('topics')
  topicsController;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  // Services
  @service
  features;

  @service
  fetch;

  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  @tracked
  currentStep = 0;
  @tracked
  steps = null;

  Scopes = Scopes;
  subjectTypes = subjectTypes;

  init() {
    super.init(...arguments);

    this.steps = topicSteps;
  }

  // Compute the available subject options
  @computed('model.allTeacherSubjects.@each.{isTopic,id}')
  get topicSubjects() {
    return this.get('model.allTeacherSubjects')
      .filter((subject) => subject.isTopic && !isEmpty(subject.id))
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.categories.@each.subjectId', 'goalSubject.id')
  get categories() {
    let selectedSubjectId = this.get('goalSubject.id');

    return this.get('model.categories')
      .filter((category) => category.subject_id === selectedSubjectId)
      .sort(byKeys('sequence', 'label'));
  }

  // Compute the available subcategories filtered by the selected category & subject
  @computed(
    'model.subcategories.@each.{categoryId,subjectId}',
    'goalCategory.id',
    'goalSubject.id',
  )
  get subcategories() {
    let selectedCategoryId = this.get('goalCategory.id');
    let selectedSubjectId = this.get('goalSubject.id');

    return this.get('model.subcategories')
      .filter(
        (subcategory) =>
          subcategory.category_id === selectedCategoryId &&
          subcategory.subject_id === selectedSubjectId,
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  activeSubject = '';

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet('urn:linced:organisation:role:admin');
  }

  @computed('isTenantAdmin', 'isAdmin')
  get readOnly() {
    return !this.isTenantAdmin && !this.isAdmin;
  }

  // State
  @tracked
  view = 'table';

  // deleteGoalSubjectDialogOpen provides the dialog opened when
  // deleting a goal subject
  @tracked
  deleteGoalSubjectDialogOpen = false;

  // deleteGoalCategoryDialogOpen provides the dialog opened when
  // deleting a goal category
  @tracked
  deleteGoalCategoryDialogOpen = false;

  @tracked
  deletingCategory = null;

  // deleteGoalSubcategoryDialogOpen provides the dialog opened when
  // deleting a goal subcategory
  @tracked
  deleteGoalSubcategoryDialogOpen = false;

  @tracked
  deletingSubcategory = null;

  // statesDeleteDialogOpen provides the dialog opened state for deleting goal
  // states.
  @tracked
  statesDeleteDialogOpen = false;

  // statesDeleteInProgress provides a state to inform the delete dialog that
  // the deletion of goal states is being processed.
  @tracked
  statesDeleteInProgress = false;

  // statesLoading provides a state to inform the async button that goal states
  // are still being queried for.
  @tracked
  statesLoading = false;

  // statesNumToDelete provides the total count of selected goal states to be
  // delete.
  @tracked statesNumToDelete = 0;

  // statesNumDeleted provides a count of the number of goal states that have
  // currently been deleted.
  @tracked statesNumDeleted = 0;

  // statesToDelete contains the array of selected goal states to be deleted.
  @tracked
  statesToDelete = null;

  isPLP = false;
  @tracked
  categoryDialogOpen = false;
  @tracked
  editingCategory = null;
  @tracked
  subcategoryDialogOpen = false;
  @tracked
  editingSubcategory = null;
  @tracked
  editTopicWizard = false;

  @computed('form.isInvalid')
  get isFormInvalid() {
    return this.get('form.isInvalid'); // Read-only computed property to prevent mutation issues
  }

  tab = 0;
  @tracked
  topicDialogOpen = false;
  @tracked
  goalSaveInProgress = false;
  @tracked
  editingGoal = null;
  @tracked
  goalLabel = '';
  @tracked
  goalEducatorDescription = '';
  @tracked
  goalPeopleDescription = '';
  @tracked
  goalExemplar = '';
  @tracked
  learningPurpose = '';
  @tracked
  learningIntentions = '';
  @tracked
  successCriteria = '';
  @tracked
  keyQuestions = '';
  @tracked
  learningConnections = '';
  @tracked
  assessmentTasks = '';
  @tracked
  teachingStrategies = '';

  // Success Criteria Language
  @tracked
  SC1 = '';

  @tracked
  SC2 = '';
  @tracked
  SC3 = '';
  @tracked
  SC4 = '';
  @tracked
  SC5 = '';

  // updateSuccessCriteria writes the per-grade success-criteria label. The grade
  // is curried in by `{{fn}}` so the input's value arrives last. `SC1`-`SC5` are
  // tracked, so the computed-key assignment goes through the tracked setter.
  @action
  updateSuccessCriteria(gradeValue, value) {
    this[`SC${gradeValue}`] = value;
  }
  @tracked
  goalLinkedGoals = [];
  @tracked
  goalPriorLinkedGoals = [];
  @tracked
  goalFutureLinkedGoals = [];

  @computed('goalLinkedGoals.[]')
  get subjectLinkedGoals() {
    let groupedGoals = {};
    this.goalLinkedGoals.forEach((goal) => {
      if (goal) {
        let subject = get(goal, 'subjectId');
        let subjectLabel = get(subject, 'label');
        let subjectType = get(subject, 'subjectTypeName') || 0;
        let category = goal.get('categoryId');
        let categoryLabel = get(category, 'label');
        // Create a key for the grouped goals using subject label, subject type label, and category label
        let key = `${subjectLabel} - ${categoryLabel}`;

        // Initialize the array for this key if it doesn't exist yet
        if (!groupedGoals[key]) {
          groupedGoals[key] = [];
        }
        // Include the subjectType as a property on the goal
        goal.set('subjectType', subjectType);
        // Push the goal into the corresponding array
        groupedGoals[key].push(goal);
      }
    });

    return groupedGoals;
  }

  @computed('goalPriorLinkedGoals.[]')
  get subjectPriorLinkedGoals() {
    let groupedGoals = {};
    this.goalPriorLinkedGoals.forEach((goal) => {
      if (goal) {
        let subject = get(goal, 'subjectId');
        let subjectLabel = get(subject, 'label');
        let subjectType = get(subject, 'subjectTypeName') || 0;
        let category = goal.get('categoryId');
        let categoryLabel = get(category, 'label');
        // Create a key for the grouped goals using subject label, subject type label, and category label
        let key = `${subjectLabel} - ${categoryLabel}`;

        // Initialize the array for this key if it doesn't exist yet
        if (!groupedGoals[key]) {
          groupedGoals[key] = [];
        }
        // Include the subjectType as a property on the goal
        goal.set('subjectType', subjectType);
        // Push the goal into the corresponding array
        groupedGoals[key].push(goal);
      }
    });

    return groupedGoals;
  }

  @computed('goalFutureLinkedGoals.[]')
  get subjectFutureLinkedGoals() {
    let groupedGoals = {};
    this.goalFutureLinkedGoals.forEach((goal) => {
      if (goal) {
        let subject = get(goal, 'subjectId');
        let subjectLabel = get(subject, 'label');
        let subjectType = get(subject, 'subjectTypeName') || 0;
        let category = goal.get('categoryId');
        let categoryLabel = get(category, 'label');
        // Create a key for the grouped goals using subject label, subject type label, and category label
        let key = `${subjectLabel} - ${categoryLabel}`;

        // Initialize the array for this key if it doesn't exist yet
        if (!groupedGoals[key]) {
          groupedGoals[key] = [];
        }
        // Include the subjectType as a property on the goal
        goal.set('subjectType', subjectType);
        // Push the goal into the corresponding array
        groupedGoals[key].push(goal);
      }
    });

    return groupedGoals;
  }

  @tracked
  entityToDelete = null;
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @tracked
  deleteDialogOpen;

  @tracked
  goalCategory;

  @tracked
  goalSubcategory;

  @tracked
  goalSubject;

  @tracked
  subcategoryParent;

  @tracked
  entityTypeToDelete;

  // Methods
  // _getGoalStates returns all the current goal states for a given school
  // based on the provided params.
  _getGoalStates(schoolId, subjectId, categoryId, subcategoryId, goalId) {
    return this.store.query('goals/state', {
      schoolId,
      subjectId,
      categoryId,
      subcategoryId,
      goalId,
    });
  }

  // statesDeletedPercent calculates the percentage of states deleted to inform
  // a determinate loading component.
  get statesDeletedPercent() {
    if (this.statesNumToDelete > 0) {
      return (this.statesNumDeleted / this.statesNumToDelete) * 100;
    }
    return 0;
  }

  @action
  setStepValue(valueKey, value) {
    this.set(valueKey, value);
  }

  @action
  setSubject(subject) {
    this.goalSubject = subject;
    this.goalCategory = null; // Reset category when subject changes
    this.goalSubcategory = null; // Reset subcategory when subject changes

    if (this.onSubjectChange) {
      this.onSubjectChange(subject);
    }
  }

  @action
  setCategory(category) {
    this.goalCategory = category;
    this.goalSubcategory = null; // Reset subcategory when category changes

    if (this.onCategoryChange) {
      this.onCategoryChange(category);
    }
  }

  @action
  setSubcategory(subcategory) {
    this.goalSubcategory = subcategory;

    if (this.onSubcategoryChange) {
      this.onSubcategoryChange(subcategory);
    }
  }

  // deleteStates performs the deletion of the selected goal states.
  @action
  deleteStates() {
    this.statesDeleteInProgress = true;
    this.statesNumDeleted = 0;

    // perform state deletion sequentially using reduce.
    const states = this.statesToDelete;
    states
      .reduce((previousPromise, nextState) => {
        return previousPromise.then(() => {
          return nextState.destroyRecord().then(() => {
            this.incrementProperty('statesNumDeleted');
            return Promise.resolve();
          });
        });
      }, Promise.resolve())
      .then(() => {
        this.statesDeleteDialogOpen = false;
        this.statesDeleteInProgress = false;

        this.paperToaster.show(`Successfully deleted Goal states.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
      })
      .catch(() => {
        this.paperToaster.show(
          `Error deleting a Goal State. Please try again.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  // getStatesToDelete performs a query to get the current states and opens
  // the delete state dialog.
  @action
  getStatesToDelete(type, entity) {
    this.statesLoading = true;

    let states;
    switch (type) {
      case 'subject':
        states = this._getGoalStates(entity.get('school.id'), entity.id);
        break;

      case 'category':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.id,
        );
        break;

      case 'subcategory':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.category_id,
          entity.id,
        );
        break;

      case 'goal':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.category_id,
          entity.subcategory_id,
          entity.id,
        );
        break;

      default:
        this.paperToaster.show(
          `Entity provided to delete goal states is not supported.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
        return;
    }

    states
      .then((states) => {
        // Close currently open edit dialogs...
        this.categoryDialogOpen = false;
        this.editTopicWizard = false;
        this.topicDialogOpen = false;
        this.subcategoryDialogOpen = false;
        this.statesLoading = false;

        if (isEmpty(states)) {
          this.paperToaster.show(`No Goal States to delete!`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
          return;
        }

        this.statesDeleteDialogOpen = true;
        this.statesNumToDelete = states.length;
        this.statesToDelete = states;
      })
      .catch(() => {
        this.statesLoading = false;
        this.paperToaster.show(`Error getting Goal States, please try again.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }

  @action
  edit() {
    this.topicsController.editSubject(this.get('model.subject'));
  }

  @action
  addCategory() {
    this.editingCategory = null;
    this.categoryDialogOpen = true;
  }

  @action
  editCategory(category) {
    this.editingCategory = category;
    this.categoryDialogOpen = true;
  }

  @action
  closeCategoryDialog() {
    this.categoryDialogOpen = false;
  }

  @action
  categorySaved() {
    this.get('model.categories').update();
  }

  @action
  addSubcategory(category) {
    this.subcategoryParent = category;
    this.editingSubcategory = null;
    this.subcategoryDialogOpen = true;
  }

  @action
  editSubcategory(subcategory) {
    this.subcategoryParent = null;
    this.editingSubcategory = subcategory;
    this.subcategoryDialogOpen = true;
  }

  @action
  closeSubcategoryDialog() {
    this.subcategoryDialogOpen = false;
  }

  @action
  subcategorySaved() {
    this.get('model.subcategories').update();
  }

  // setGoalDescription sets the label to be the educators description in
  // order to reduce typing/copy+paste.
  @action
  setGoalDescription(description) {
    this.goalLabel = description;
    this.goalEducatorDescription = description;
  }

  @action
  addGoal(category, subcategory) {
    this.resetGoal();
    this.goalSubject = this.model.subject;
    this.goalCategory = category;
    this.goalSubcategory = subcategory;
    this.goalLinkedGoals = [];
    this.goalPriorLinkedGoals = [];
    this.goalFutureLinkedGoals = [];
    this.editTopicWizard = true;
  }

  @action
  async editGoal(goal) {
    this.resetGoal();

    this.editingGoal = goal;
    // A belongsTo takes a record, not the async proxy.
    const { subject, category, subcategory } = await hash({
      subject: goal.subject,
      category: goal.category,
      subcategory: goal.subcategory,
    });

    this.goalSubject = subject;
    this.goalCategory = category;
    this.goalSubcategory = subcategory;
    this.goalLabel = goal.get('label');
    this.goalEducatorDescription = goal.get('educatorDescription');
    this.goalPeopleDescription = goal.get('peopleDescription');
    this.goalExemplar = goal.get('exemplar');
    this.learningPurpose = goal.get('learningPurpose');
    this.learningIntentions = goal.get('learningIntentions');
    this.successCriteria = goal.get('successCriteria');
    this.keyQuestions = goal.get('keyQuestions');
    this.learningConnections = goal.get('learningConnections');
    this.assessmentTasks = goal.get('assessmentTasks');
    this.teachingStrategies = goal.get('teachingStrategies');
    this.SC1 = goal.get('SC1');
    this.SC2 = goal.get('SC2');
    this.SC3 = goal.get('SC3');
    this.SC4 = goal.get('SC4');
    this.SC5 = goal.get('SC5');

    // resolve the async hasMany relationship upfront.
    [
      this.goalLinkedGoals,
      this.goalPriorLinkedGoals,
      this.goalFutureLinkedGoals,
    ] = (
      await Promise.all([
        goal.linkedGoalIds,
        goal.priorLinkedGoalIds,
        goal.futureLinkedGoalIds,
      ])
    ).map((goals) => goals.slice());
    this.editTopicWizard = true;
  }

  @action
  closeGoalDialog() {
    this.editTopicWizard = false;
    this.currentStep = 0;
  }

  @action
  closeTopicDialog() {
    this.topicDialogOpen = false;
    this.currentStep = 0;
  }

  @action
  saveGoal() {
    let goal = this.editingGoal;
    let toaster = this.paperToaster;
    this.goalSaveInProgress = true;

    try {
      if (isEmpty(goal)) {
        let category = this.goalCategory;
        let subcategory = this.goalSubcategory;

        // Validate required fields
        if (!category) {
          throw new Error('Category is required');
        }

        if (!this.goalLabel) {
          throw new Error('Goal label is required');
        }

        let sequence =
          (isEmpty(subcategory)
            ? category.get('goals.length')
            : subcategory.get('goals.length')) + 1;

        goal = this.store.createRecord('goals/goal', {
          schoolId: this.get('session.school'),
          authorId: this.get('session.person'),
          subjectId: this.goalSubject,
          categoryId: this.goalCategory,
          subcategoryId: this.goalSubcategory,
          sequence,
        });
      }

      let params = {
        subjectId: this.goalSubject,
        categoryId: this.goalCategory,
        subcategoryId: this.goalSubcategory,
        label: this.goalLabel,
        educatorDescription: this.goalEducatorDescription,
        peopleDescription: this.goalEducatorDescription,
        exemplar: this.goalExemplar,
        learningIntentions: this.learningIntentions,
        successCriteria: this.successCriteria,
        keyQuestions: this.keyQuestions,
        learningConnections: this.learningConnections,
        assessmentTasks: this.assessmentTasks,
        teachingStrategies: this.teachingStrategies,
        linkedGoals: this.goalLinkedGoals,
        priorLinkedGoals: this.goalPriorLinkedGoals,
        futureLinkedGoals: this.goalFutureLinkedGoals,
        SC1: this.SC1,
        SC2: this.SC2,
        SC3: this.SC3,
        SC4: this.SC4,
        SC5: this.SC5,
      };

      goal.setProperties(params);

      goal
        .save()
        .then(() => {
          this.editTopicWizard = false;
          this.currentStep = 0;
          toaster.show(
            `${this.get('strings.singularTopicLabel')} "${goal.get(
              'label',
            )}" saved.`,
            {
              duration: 4000,
              position: 'top right',
              toastClass: 'md-accent',
            },
          );
        })
        .catch((error) => {
          // Unload a new record and revert an edited one; the form keeps the input.
          rollbackRecord(goal);
          let errorMessage = 'Failed to save topic.';
          if (error && error.errors && error.errors.length) {
            // Extract error message from the API response if available
            errorMessage = error.errors
              .map((e) => e.detail || e.title)
              .join('. ');
          } else if (error && error.message) {
            errorMessage = error.message;
          }

          this.toaster.error(errorMessage);
        })
        .finally(() => {
          this.goalSaveInProgress = false;
        });
    } catch (error) {
      toaster.error(error.message || 'Failed to prepare topic for saving.');
      this.goalSaveInProgress = false;
    }
  }

  // bulkDelete posts to one of the goals API's :bulkdelete endpoints. On
  // failure it shows the error and rejects with BULK_DELETE_FAILED, so the
  // caller stops before deleting the parent record.
  bulkDelete(collection, data) {
    let uri = `${config.APP.apiHost}/goals/${config.APP.apiPath}/${collection}/:bulkdelete`;
    return this.fetch.post(uri, { data }).catch(() => {
      this.toaster.error(
        `An error occurred while deleting ${collection}. Please try again.`,
      );
      throw BULK_DELETE_FAILED;
    });
  }

  @action
  deleteGoalCategory(category) {
    let promises = [];
    let data = {
      schoolId: category.get('school.id'),
      subjectId: category.subject_id,
      categoryId: category.id,
    };
    if (category.goals.length) {
      promises.push(this.bulkDelete('goals', data));
    }
    if (category.subcategories.length) {
      promises.push(this.bulkDelete('subcategories', data));
    }

    return Promise.all(promises)
      .then(() => destroyOrRestore(category))
      .then(() => {
        this.deleteGoalCategoryDialogOpen = false;
        this.toaster.success('Goal Category has been deleted successfully.');
      })
      .catch((err) => {
        if (err !== BULK_DELETE_FAILED) {
          this.toaster.error(
            'An error occurred while deleting Subject items. Please try again.',
          );
        }
      });
  }

  @action
  deleteGoalSubcategory(subcategory) {
    let promises = [];
    if (subcategory.goals.length) {
      subcategory.goals.forEach((goal) => {
        promises.push(destroyOrRestore(goal));
      });
    }

    return Promise.all(promises)
      .then(() => destroyOrRestore(subcategory))
      .then(() => {
        this.deleteGoalSubcategoryDialogOpen = false;
        this.toaster.success('Goal Subcategory has been deleted successfully.');
      })
      .catch(() => {
        this.toaster.error(
          'An error occurred while deleting the subcategory. Please try again.',
        );
      });
  }

  @action
  deleteGoalSubjectItems(entity) {
    let data = {
      schoolId: entity.get('school.id'),
      subjectId: entity.id,
    };
    let promises = [];
    // Bulk delete goals
    if (this.model.goals.length) {
      promises.push(this.bulkDelete('goals', data));
    }

    // Bulk delete goal categories/subcategories
    if (this.model.categories.length) {
      promises.push(this.bulkDelete('categories', data));
      if (this.model.subcategories.length) {
        promises.push(this.bulkDelete('subcategories', data));
      }
    }

    // Only clear the subject's categories and delete it once every child
    // was deleted; a failed bulk delete used to be swallowed here.
    return Promise.all(promises)
      .then(() => {
        this.set('model.subject.categories', []);
        this.deleteGoalSubjectDialogOpen = false;
        return destroyOrRestore(entity);
      })
      .then(() => {
        // The entire subject was deleted; navigate back to the Goals admin page
        this.router.transitionTo('goals');
        this.toaster.success('Goal Subject has been deleted successfully.');
      })
      .catch((err) => {
        if (err !== BULK_DELETE_FAILED) {
          this.toaster.error(
            'An error occurred while deleting Goal Subject. Please try again.',
          );
        }
      });
  }

  @action
  openDeleteSubjectDialog(entity) {
    this._getGoalStates(entity.get('school.id'), entity.id).then((states) => {
      this.statesToDelete = states;
      this.deleteGoalSubjectDialogOpen = true;
    });
  }

  @action
  openDeleteCategoryDialog(entity) {
    this._getGoalStates(
      entity.get('school.id'),
      entity.subject_id,
      entity.id,
    ).then((states) => {
      this.statesToDelete = states;
      this.deletingCategory = entity;
      this.deleteGoalCategoryDialogOpen = true;
    });
  }

  @action
  openDeleteSubcategoryDialog(entity) {
    this._getGoalStates(
      entity.get('school.id'),
      entity.subject_id,
      entity.category_id,
      entity.id,
    ).then((states) => {
      this.statesToDelete = states;
      this.deletingSubcategory = entity;
      this.deleteGoalSubcategoryDialogOpen = true;
    });
  }

  @action
  confirmDeleteEntity(type, entity) {
    this.entityTypeToDelete = type;
    this.entityToDelete = entity;
    this.deleteDialogOpen = true;
  }

  @action
  closeDeleteDialog() {
    this.deleteDialogOpen = false;
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new parent category/subcategory - update the relationship.
      // A category's own list has no subcategory.
      item.setProperties({
        categoryId: targetList.__category__,
        subcategoryId: targetList.__subcategory__ ?? null,
      });
    }

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      let saved = entity.save();
      if (moved && entity === item) {
        // Put it back under its old category/subcategory; the lists are
        // built from the relationships, so it moves back on screen too.
        saved.catch(() => {
          rollbackRecord(entity);
          this.toaster.error(`Error moving ${entity.get('label')}`);
        });
      }
    });
  }

  // toggleLinkedGoal/togglePriorGoal/toggleFutureGoal are called by
  // GoalLinker (handleToggleLinkedGoal/onTogglePriorGoal/onToggleFutureGoal)
  // instead of GoalLinker mutating goalLinkedGoals/etc directly - these are
  // plain, reassigned @tracked arrays, and a goal can only be linked from
  // one of the three lists at a time, so adding to one removes it from the
  // other two (matching GoalLinker's previous internal toggleGoal logic).
  @action
  toggleLinkedGoal(goal) {
    if (this.goalLinkedGoals.includes(goal)) {
      this.goalLinkedGoals = this.goalLinkedGoals.filter((g) => g !== goal);
    } else {
      this.goalLinkedGoals = [...this.goalLinkedGoals, goal];
      this.goalPriorLinkedGoals = this.goalPriorLinkedGoals.filter(
        (g) => g !== goal,
      );
      this.goalFutureLinkedGoals = this.goalFutureLinkedGoals.filter(
        (g) => g !== goal,
      );
    }
  }

  @action
  togglePriorGoal(goal) {
    if (this.goalPriorLinkedGoals.includes(goal)) {
      this.goalPriorLinkedGoals = this.goalPriorLinkedGoals.filter(
        (g) => g !== goal,
      );
    } else {
      this.goalPriorLinkedGoals = [...this.goalPriorLinkedGoals, goal];
      this.goalLinkedGoals = this.goalLinkedGoals.filter((g) => g !== goal);
      this.goalFutureLinkedGoals = this.goalFutureLinkedGoals.filter(
        (g) => g !== goal,
      );
    }
  }

  @action
  toggleFutureGoal(goal) {
    if (this.goalFutureLinkedGoals.includes(goal)) {
      this.goalFutureLinkedGoals = this.goalFutureLinkedGoals.filter(
        (g) => g !== goal,
      );
    } else {
      this.goalFutureLinkedGoals = [...this.goalFutureLinkedGoals, goal];
      this.goalLinkedGoals = this.goalLinkedGoals.filter((g) => g !== goal);
      this.goalPriorLinkedGoals = this.goalPriorLinkedGoals.filter(
        (g) => g !== goal,
      );
    }
  }

  @action
  setView(newState) {
    this.view = newState;
  }

  @action
  removeLinkedGoal(goal) {
    this.goalLinkedGoals = this.goalLinkedGoals.filter((g) => g !== goal);
  }

  @action
  removePriorLinkedGoal(goal) {
    this.goalPriorLinkedGoals = this.goalPriorLinkedGoals.filter(
      (g) => g !== goal,
    );
  }

  @action
  removeFutureLinkedGoal(goal) {
    this.goalFutureLinkedGoals = this.goalFutureLinkedGoals.filter(
      (g) => g !== goal,
    );
  }

  resetGoal() {
    this.editingGoal = null;
    this.goalLabel = '';
    this.goalEducatorDescription = '';
    this.goalPeopleDescription = '';
    this.goalExemplar = '';
    this.learningPurpose = '';
    this.learningIntentions = '';
    this.successCriteria = '';
    this.keyQuestions = '';
    this.learningConnections = '';
    this.assessmentTasks = '';
    this.teachingStrategies = '';
    this.goalLinkedGoals = [];
    this.goalPriorLinkedGoals = [];
    this.goalFutureLinkedGoals = [];
    this.SC1 = '';
    this.SC2 = '';
    this.SC3 = '';
    this.SC4 = '';
    this.SC5 = '';
  }
}
